import {ALLOWED_IMAGE_FILE_TYPES, ALLOWED_VIDEO_TYPE} from '../shared/constants';
import {DatePipe} from '@angular/common';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../environments/environment';
import { lastValueFrom, of } from 'rxjs';
import { LookupModel } from 'app/models/shared/lookup.model';
import { SearchResponseModel } from 'app/models/shared';
import { AppStorage } from './storage';

export class Utility {
  public constructor() {
  }

  public static generateUUID() {
    const s4 = () =>
      Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  }

  private static getItemIndexAvailable(searchResult: any, itemIndex: number, isNext: boolean): number {
    if (itemIndex === -1) {
      return searchResult.length - 1;
    }

    for (let i = itemIndex; i < searchResult.length; isNext ? i++ : i--) {
      if (i === -1) {
        i = searchResult.length - 1;
      }

      if (i === searchResult.length) {
        i = 0;
      }

      if (searchResult[i].isJoined) {
        const indexAvailable = this.getItemIndexAvailable(searchResult, isNext ? i + 1 : i - 1, isNext);
        if (indexAvailable === -1) {
          return -1;
        }
      } else {
        return i;
      }
    }

    return -1;
  }

  public static onStanderListArrowUpPressed(instance: any): any {
    const searchedItems = instance.option('items');
    const selectedItems = instance.option('selectedItems');
    const currentItemIndex = searchedItems.indexOf(selectedItems[0]);
    let itemIndexAvailable = -1;
    if (currentItemIndex > 0) {
      itemIndexAvailable = this.getItemIndexAvailable(searchedItems, currentItemIndex - 1, false);
    } else {
      const lastItemIndex = searchedItems.length - 1;
      itemIndexAvailable = this.getItemIndexAvailable(searchedItems, lastItemIndex, false);
    }

    if (itemIndexAvailable !== -1) {
      instance.selectItem(itemIndexAvailable);
      instance.scrollToItem(itemIndexAvailable);
      return searchedItems[itemIndexAvailable];
    }

    return undefined;
  }

  public static onStanderListArrowDownPressed(instance: any): any {
    const searchedItems = instance.option('items');
    const selectedItems = instance.option('selectedItems');
    const currentItemIndex = searchedItems.indexOf(selectedItems[0]);

    let nextItemIndexAvailable = -1;
    if (selectedItems.length === 0 || currentItemIndex >= searchedItems.length - 1) {
      nextItemIndexAvailable = this.getItemIndexAvailable(searchedItems, 0, true);
    } else {
      nextItemIndexAvailable = this.getItemIndexAvailable(searchedItems, currentItemIndex + 1, true);
    }

    if (nextItemIndexAvailable !== -1) {
      instance.selectItem(nextItemIndexAvailable);
      instance.scrollToItem(nextItemIndexAvailable);
      return searchedItems[nextItemIndexAvailable];
    }

    return undefined;
  }

  public static isImageExtension(fileName: string): boolean {
    return ALLOWED_IMAGE_FILE_TYPES.some((_: string) => _.toLowerCase() === this.getFileExtension(fileName).toLowerCase());
  }

  public static isVideoExtension(fileName: string): boolean {
    return ALLOWED_VIDEO_TYPE.some((_: string) => _.toLowerCase() === this.getFileExtension(fileName).toLowerCase());
  }

  public static isPDFExtension(fileName: string): boolean {
    return this.getFileExtension(fileName).toLowerCase() === '.pdf';
  }

  public static isWordExtension(fileName: string): boolean {
    return ['.doc','.docx','.dotx','.rtf'].indexOf(this.getFileExtension(fileName).toLowerCase()) >= 0;
  }

  public static isSpreadSheetExtension(file: string): boolean {
    return ['.xls', '.xlsx', '.xltx'].indexOf(this.getFileExtension(file).toLowerCase()) >= 0;
  }

  public static getFileExtension(fileName: string): string {
    const nameArray = fileName.split('.');
    return `.${nameArray[nameArray.length - 1]}`;
  }

  public static getFileName(fullPath: string): string {
    return fullPath.replace(/^.*[\\\/]/, '')
  }

  public static removeAllWhiteSpaces(text: string): string {
    if (text != null) {
      return text.replace(/\s/g, '');
    }

    return text;
  }

  public static formatToDate(value: Date, format = 'yyyy-MM-dd'): string | null {
    const datePipe = new DatePipe('en-US');

    if (typeof (value) === 'string') {
      value = new Date(value);
    }
    return datePipe.transform(value, format);
  }

  public static convertDateToString(date: Date, format = 'yyyy-MM-dd'): string | null {
    return this.formatToDate(date, format);
  }

  public static isWeekend(date: Date): boolean {
    const dayOfWeek = date.getDay();
    return (dayOfWeek === 6) || (dayOfWeek === 0);
  }

  public static encrypt(msg: string, pass: string): string {
    // random salt for derivation
    const keySize = 256;
    const salt = CryptoJS.lib.WordArray.random(16);
    // well known algorithm to generate key
    const key = CryptoJS.PBKDF2(pass, salt, {
      keySize: keySize / 32,
      iterations: 100
    });
    // random IV
    const iv = CryptoJS.lib.WordArray.random(128 / 8);
    // specify everything explicitly
    const encrypted = CryptoJS.AES.encrypt(msg, key, {
      iv: iv,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC
    });
    // combine everything together in base64 string
    return CryptoJS.enc.Base64.stringify(salt.concat(iv).concat(encrypted.ciphertext));
  }

  public static encodeURI(value: string): string {
    return encodeURIComponent(this.encrypt(value, environment.encode));
  }

  public static copyClipboard(value: string) {
    // Get the text field      
    const input = document.createElement("input");
    document.body.appendChild(input);
    input.hidden = true;
    input.value = value;
    input.id = "copyInput";
    
    // Select the text field
    input.select();
    input.setSelectionRange(0, Number.MAX_SAFE_INTEGER); // For mobile devices
  
     // Copy the text inside the text field
    navigator.clipboard.writeText(input.value);
    document.body.removeChild(input);
  }

  public static getLocalDateTimeString() {
    const locale = AppStorage.getLocalStorageDataRaw('X-User-Locale') || 'en-US';
    return new Date().toLocaleString(locale);
  }

  public static toLocalDateTimeString(value: string | number | Date) {
    const locale = AppStorage.getLocalStorageDataRaw('X-User-Locale') || 'en-US';
    return value.toLocaleString(locale);
  }


  public static getLocalDataSource(data = []) {
    return lastValueFrom<SearchResponseModel<LookupModel<any>>>(of(new SearchResponseModel<LookupModel<any>>({
        data: data,
        totalCount: data.length
    })));
  }

  public static formatTimelineNotes (data: any[]) {
    const formattedNotes = data.map(note => {
      if (!note.created_at || !note.created_by || !note.content) return null;
      
      const locale = AppStorage.getLocalStorageDataRaw('X-User-Locale') || 'en-US';

      const localDate = new Date(note.created_at).toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
  
      return `[${localDate}] ${note.created_by}: ${note.content}`;
    }).filter(Boolean); // remove nulls

    return formattedNotes.join('\r\n');
  };
  
}
